// topLayerTip — a hover/tap tooltip that renders in the browser TOP LAYER via the
// Popover API, so it is NEVER clipped by an ancestor's overflow. A plain DaisyUI
// `::before` tooltip gets cut off here: the CMS image tile is `overflow-hidden`
// (to round the thumbnail) and sits in a scrolling grid inside a modal <dialog>.
// The trigger and the tip live in the same Alpine wrapper; on show we promote the
// tip to the top layer and position it next to the trigger.
//
// Usage (templ):
//   <span x-data="topLayerTip" @click.outside="hide()" class="relative …">
//     <button x-ref="trigger" @mouseenter="show()" @mouseleave="hide()"
//             @focus="show()" @blur="hide()" @click.stop="toggle()"
//             aria-label="…">…icon…</button>
//     <div x-ref="tip" class="studio-tip" role="tooltip">…copy…</div>
//   </span>
//
// Progressive enhancement: with no Popover API the tip falls back to an
// absolutely-positioned element via the `studio-tip--open` class (may clip, but
// stays styled and single). `aria-label` on the trigger carries the text for
// assistive tech in every case, so we deliberately DON'T set a native `title`
// (which would double up with the styled bubble).
export function topLayerTip() {
  return {
    _supported: false,

    init() {
      this._supported = typeof (HTMLElement.prototype as any).showPopover === "function";
      const tip = (this as any).$refs.tip as HTMLElement | undefined;
      // Mark the tip as a manual popover only when supported, so an
      // unsupported browser isn't left with a popover it can't open (it uses
      // the class fallback instead). CSS hides .studio-tip by default and
      // shows it on :popover-open (supported) or .studio-tip--open (fallback).
      if (tip && this._supported) tip.setAttribute("popover", "manual");
    },

    show() {
      const tip = (this as any).$refs.tip as
        (HTMLElement & { showPopover?: () => void }) | undefined;
      const trigger = (this as any).$refs.trigger as HTMLElement | undefined;
      if (!tip || !trigger) return;
      if (this._supported && typeof tip.showPopover === "function") {
        try {
          if (!tip.matches(":popover-open")) tip.showPopover();
          // Position AFTER showPopover so offsetWidth/Height are measurable;
          // the override runs before paint, so there is no visible jump.
          this._position(tip, trigger);
        } catch {
          /* already showing / detached — ignore */
        }
      } else {
        tip.classList.add("studio-tip--open");
      }
    },

    hide() {
      const tip = (this as any).$refs.tip as
        (HTMLElement & { hidePopover?: () => void }) | undefined;
      if (!tip) return;
      if (this._supported && typeof tip.hidePopover === "function") {
        try {
          if (tip.matches(":popover-open")) tip.hidePopover();
        } catch {
          /* not open — ignore */
        }
      } else {
        tip.classList.remove("studio-tip--open");
      }
    },

    toggle() {
      const tip = (this as any).$refs.tip as HTMLElement | undefined;
      if (!tip) return;
      const open = this._supported
        ? tip.matches(":popover-open")
        : tip.classList.contains("studio-tip--open");
      if (open) this.hide();
      else this.show();
    },

    // Place the tip to the right of the trigger; flip left if it would overflow
    // the viewport, then clamp vertically. Fixed positioning (the popover is in
    // the top layer, so coordinates are viewport-relative).
    _position(tip: HTMLElement, trigger: HTMLElement) {
      const r = trigger.getBoundingClientRect();
      const gap = 8;
      tip.style.position = "fixed";
      tip.style.margin = "0";
      tip.style.inset = "auto";
      const tw = tip.offsetWidth;
      const th = tip.offsetHeight;
      let left = r.right + gap;
      if (left + tw > window.innerWidth - gap) left = r.left - tw - gap; // flip left
      if (left < gap) left = gap;
      let top = r.top;
      if (top + th > window.innerHeight - gap) top = window.innerHeight - th - gap;
      if (top < gap) top = gap;
      tip.style.left = `${left}px`;
      tip.style.top = `${top}px`;
    },
  };
}
